import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, Drawer, IconButton, MenuItem, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./styles";
import CrmService, { academicYearLabel, apiError, childAge, studentFormPath, studentRemovalWarning, timeInTz, todayYmd, ymdInTz } from "./crmService";
import { showAppConfirm } from "../../../context/AlertContext";
import { useCrmSettings, useDuplicateMobile, withExtras } from "./crmUi";

const EMPTY = {
  enquiryDate: "",
  mobile: "",
  alternatePhone: "",
  email: "",
  childName: "",
  childDob: "",
  motherName: "",
  fatherName: "",
  gender: "",
  program: "",
  academicYear: "",
  currentSchool: "",
  area: "",
  address: "",
  source: "",
  sourceOther: "",
  referrerName: "",
  siblingStudentId: "",
  assignedTo: "",
  status: "New",
  lostReason: "",
  nextFollowUpDate: "",
  notes: "",
};

const fromEnquiry = (e, tz) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, e[k] == null ? "" : e[k]])),
  enquiryDate: ymdInTz(e.enquiryDate, tz),
  childDob: ymdInTz(e.childDob, tz),
  nextFollowUpDate: ymdInTz(e.nextFollowUpDate, tz),
  siblingStudentId: e.siblingStudentId ? String(e.siblingStudentId) : "",
});

/** Right-hand drawer with the full enquiry form (add or edit). */
export default function EnquiryFormDrawer({ open, enquiry, onClose, onSaved }) {
  const classes = useStyles();
  const history = useHistory();
  const { settings, options, tz } = useCrmSettings();
  const [form, setForm] = useState(EMPTY);
  const [sibling, setSibling] = useState(null);
  const [siblingOptions, setSiblingOptions] = useState([]);
  // Running + future academic years from Academic Master; a new enquiry starts on the default.
  const [years, setYears] = useState({ options: [], defaultYear: "" });
  useEffect(() => {
    if (!open) return;
    CrmService.academicYears()
      .then((res) => setYears({ options: res?.options || [], defaultYear: res?.defaultYear || "" }))
      .catch(() => setYears({ options: [], defaultYear: "" }));
  }, [open]);
  useEffect(() => {
    // Also after the form resets (it may reset after the years arrive).
    if (open && !enquiry?._id && years.defaultYear && !form.academicYear) {
      setForm((f) => ({ ...f, academicYear: years.defaultYear }));
    }
  }, [open, enquiry, years.defaultYear, form.academicYear]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const searchTimer = useRef(null);
  const editing = Boolean(enquiry?._id);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setMessage("");
    if (enquiry?._id) {
      setForm(fromEnquiry(enquiry, tz));
      setSibling(enquiry.siblingStudentId ? { _id: String(enquiry.siblingStudentId), name: enquiry.siblingStudentName, admissionNumber: "" } : null);
    } else {
      const staff = settings?.staff || [];
      setForm({
        ...EMPTY,
        enquiryDate: todayYmd(tz),
        assignedTo: staff.length === 1 ? staff[0].name : "",
      });
      setSibling(null);
    }
  }, [open, enquiry, settings, tz]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  // New enquiries only: an edit keeps its own number and is not blocked.
  const duplicate = useDuplicateMobile(form.mobile, CrmService.checkMobile, open && !editing);
  const altDuplicate = useDuplicateMobile(form.alternatePhone, CrmService.checkMobile, open && !editing);
  const liveError = { mobile: duplicate, alternatePhone: altDuplicate };
  const fieldError = (name) => errors[name] || liveError[name] || "";
  const field = (name, extra = {}) => ({
    value: form[name],
    onChange: (e) => set({ [name]: e.target.value }),
    error: Boolean(fieldError(name)),
    helperText: fieldError(name) || extra.helperText,
    variant: "outlined",
    size: "small",
    fullWidth: true,
    ...extra,
  });

  const age = useMemo(() => (form.childDob ? childAge({ childDob: form.childDob }) : ""), [form.childDob]);
  const closedStatuses = options?.closedStatuses || ["Admitted", "Lost"];

  const searchSiblings = (q) => {
    clearTimeout(searchTimer.current);
    if (!q || q.length < 2) return;
    searchTimer.current = setTimeout(() => {
      CrmService.searchStudents(q)
        .then((res) => setSiblingOptions(res.students || []))
        .catch(() => setSiblingOptions([]));
    }, 300);
  };

  const save = async () => {
    if (editing && enquiry.status === "Admitted" && enquiry.studentId && form.status !== "Admitted") {
      const ok = await showAppConfirm(`Change ${enquiry.enquiryNo} from Admitted to ${form.status}?${studentRemovalWarning(enquiry)}`);
      if (!ok) return;
    }
    setSaving(true);
    setErrors({});
    setMessage("");
    const body = { ...form, siblingStudentId: form.source === "Sibling" ? sibling?._id || "" : "" };
    if (editing && enquiry.nextFollowUpDate && ymdInTz(enquiry.nextFollowUpDate, tz) === form.nextFollowUpDate) {
      // Keep the saved time (e.g. a 2 PM visit) when the date was not changed.
      const t = timeInTz(enquiry.nextFollowUpDate, tz);
      if (t) body.nextFollowUpDate = `${form.nextFollowUpDate}T${t}`;
    }
    try {
      const res = editing ? await CrmService.update(enquiry._id, body) : await CrmService.create(body);
      onSaved && onSaved(res);
      // Just marked Admitted: go to student registration, pre-filled from this enquiry.
      const saved = res?.enquiry;
      if (saved?._id && saved.status === "Admitted" && !saved.studentId && (!editing || enquiry.status !== "Admitted")) {
        history.push(studentFormPath(saved._id));
      }
    } catch (err) {
      const e = apiError(err);
      setErrors(e.errors);
      // A duplicate parent is explained in full, not just highlighted.
      setMessage(Object.keys(e.errors).length && !e.errors.mobile ? "Please fix the highlighted fields." : e.message);
    } finally {
      setSaving(false);
    }
  };

  // Options come from CRM settings; a value the enquiry already has but settings
  // no longer list (renamed or imported) stays selectable so editing keeps it.
  const select = (name, label, list, extra = {}) => (
    <TextField select label={label} {...field(name, extra)}>
      {extra.allowEmpty !== false && (
        <MenuItem value="">
          <em>Not set</em>
        </MenuItem>
      )}
      {withExtras(list, form[name]).map((o) => (
        <MenuItem key={o} value={o}>
          {o}
          {(list || []).includes(o) || o === "Online form" ? "" : " (not in settings)"}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Drawer anchor="right" open={open} onClose={saving ? undefined : onClose}>
      <div className={classes.drawer}>
        <div className={classes.drawerHead}>
          <div>
            <h2 className={classes.drawerTitle}>{editing ? `Edit ${enquiry.enquiryNo}` : "Add enquiry"}</h2>
            <div className={classes.muted}>
              {editing
                ? `Added by ${enquiry.createdBy?.name || "-"}`
                : "Enquiry ID is created when you save. Saved as added by you."}
            </div>
          </div>
          <IconButton aria-label="Close" onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={classes.drawerBody}>
          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Parent / guardian</h3>
            <div className={classes.grid2}>
              <TextField
                label="Mother's name *"
                autoFocus={!editing}
                {...field("motherName")}
              />
              <TextField label="Father's name" {...field("fatherName")} />
              <TextField label="Mobile *" {...field("mobile")} inputProps={{ inputMode: "tel" }} />
              <TextField label="Alternate phone" {...field("alternatePhone")} inputProps={{ inputMode: "tel" }} />
              <TextField label="Email *" {...field("email")} type="email" />
            </div>
          </section>

          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Child</h3>
            <div className={classes.grid2}>
              <TextField label="Student name *" {...field("childName")} className={classes.full} />
              <TextField
                label="Date of birth *"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: todayYmd(tz) }}
                {...field("childDob", { helperText: age ? `Age today: ${age}` : "" })}
              />
              {/* Male / Female only; an older "Other" stays shown on that enquiry. */}
              {select("gender", "Gender", (options?.genders || []).filter((g) => g !== "Other"))}
            </div>
          </section>

          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Admission</h3>
            <div className={classes.grid2}>
              {select("program", "Class *", settings?.programs)}
              <TextField
                select
                label="Academic year *"
                {...field("academicYear", {
                  helperText: !years.options.length ? "Add the academic year in Academic Master first." : "",
                })}
              >
                {years.options.map((o) => (
                  <MenuItem key={o._id} value={o.name}>
                    {academicYearLabel(o)}
                  </MenuItem>
                ))}
                {form.academicYear && !years.options.some((o) => o.name === form.academicYear) && (
                  <MenuItem value={form.academicYear}>{form.academicYear} (past)</MenuItem>
                )}
              </TextField>
              <TextField label="Current school / day care" {...field("currentSchool")} />
              <TextField label="Enquiry date" type="date" InputLabelProps={{ shrink: true }} {...field("enquiryDate")} />
              <TextField label="Area / locality *" {...field("area")} />
              <TextField label="Address" {...field("address")} multiline minRows={1} />
            </div>
          </section>

          <section className={classes.section}>
            <h3 className={classes.sectionTitle}>Source and follow-up</h3>
            <div className={classes.grid2}>
              {select("source", "Source", settings?.sources)}
              {form.source === "Others" && <TextField label="Where did it come from? *" {...field("sourceOther")} />}
              {form.source === "Referral" && <TextField label="Referred by *" placeholder="Parent, staff or other" {...field("referrerName")} />}
              {form.source === "Sibling" && (
                <Autocomplete
                  options={siblingOptions}
                  value={sibling}
                  onChange={(_, v) => setSibling(v)}
                  onInputChange={(_, q, reason) => reason === "input" && searchSiblings(q)}
                  getOptionLabel={(o) => (o ? `${o.name}${o.admissionNumber ? ` (${o.admissionNumber})` : ""}` : "")}
                  getOptionSelected={(o, v) => String(o._id) === String(v._id)}
                  filterOptions={(x) => x}
                  noOptionsText="Type 2+ letters of the student's name"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sibling studying here (optional)"
                      variant="outlined"
                      size="small"
                      error={Boolean(errors.siblingStudentId)}
                      helperText={errors.siblingStudentId}
                    />
                  )}
                />
              )}
              {/* Assigned to: hidden for now (Staff tab in CRM settings is also hidden).
              {(settings?.staff || []).length > 0
                ? select("assignedTo", "Assigned to", settings.staff.map((s) => s.name))
                : <TextField label="Assigned to" {...field("assignedTo", { helperText: "Add staff in CRM settings to pick from a list." })} />}
              */}
              {select("status", "Status", options?.statuses, { allowEmpty: false })}
              {form.status === "Lost" && select("lostReason", "Lost reason *", settings?.lostReasons)}
              {!closedStatuses.includes(form.status) && (
                <TextField
                  label="Next follow-up"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  {...field("nextFollowUpDate", { helperText: !editing && !form.nextFollowUpDate ? "Leave empty to follow up today." : "" })}
                />
              )}
              <TextField label="Notes" {...field("notes")} multiline minRows={3} className={classes.full} />
            </div>
          </section>
          {message && <div className={classes.error}>{message}</div>}
        </div>

        <div className={classes.drawerFoot}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving || Boolean(duplicate || altDuplicate)}
            startIcon={saving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : null}
          >
            {editing ? "Save changes" : "Add enquiry"}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
